import type {
  AssessmentSchemes,
  HospitalDocuments,
  PaperAnamneseForms,
  PPRDocuments,
  Referral,
  SchoolCompletionForms,
} from '@/hooks/useReferrals';

export type ReferralFileSlot = {
  key: string;
  category: string;
  fieldName: string;
  label: string;
  group: string;
};

function slot(
  category: string,
  fieldName: string,
  label: string,
  group: string,
): ReferralFileSlot {
  return {
    key: `${category}:${fieldName}`,
    category,
    fieldName,
    label,
    group,
  };
}

/** All checklist slots that support file upload on Sagsakter. */
export const ALL_REFERRAL_FILE_SLOTS: ReferralFileSlot[] = [
  // PPR
  slot('ppr', 'wisc_v_forside', 'WISC V forside eller computerudskrift', 'PPR'),
  slot('ppr', 'wisc_v_rapport', 'WISC V rapport', 'PPR'),
  slot('ppr', 'wppsi_iv_forside', 'WPPSI-IV forside', 'PPR'),
  slot('ppr', 'wppsi_iv_rapport', 'WPPSI-IV rapport', 'PPR'),
  // Hospital
  slot('hospital', 'journaludskrift', 'Journaludskrift', 'Hospital'),
  slot('hospital', 'scannings_svar_mr', 'Scannings-svar · MR', 'Hospital'),
  slot('hospital', 'scannings_svar_ct', 'Scannings-svar · CT', 'Hospital'),
  // Paper anamnese
  slot('paper_anamnese', 'smaaboern', 'Småbørn', 'Papir Anamneseskema'),
  slot('paper_anamnese', 'smaaboern_genhenvisning', 'Småbørn genhenvisning', 'Papir Anamneseskema'),
  slot('paper_anamnese', 'skoleboern', 'Skolebørn', 'Papir Anamneseskema'),
  slot('paper_anamnese', 'skoleboern_genhenvisning', 'Skolebørn genhenvisning', 'Papir Anamneseskema'),
  slot('paper_anamnese', 'unge_15_plus', 'Unge 15+', 'Papir Anamneseskema'),
  slot('paper_anamnese', 'unge_15_plus_genhenvisning', 'Unge 15+ genhenvisning', 'Papir Anamneseskema'),
  slot('paper_anamnese', 'tillaeg_til_anamnese_13_18', 'Tillæg til anamneseskema (13-18 år)', 'Papir Anamneseskema'),
  slot('paper_anamnese', 'nordisk_sporgeskema_2_5', 'Nordisk spørgeskema (2-5 år)', 'Papir Anamneseskema'),
  slot('paper_anamnese', 'dupaul_foraeldre_skole', 'DUPAUL forældre // skole', 'Papir Anamneseskema'),
  // School
  slot('school_completion', 'førskole', 'Førskole', 'Skema til udfyldelse'),
  slot('school_completion', 'indskoling', 'Indskoling', 'Skema til udfyldelse'),
  slot('school_completion', 'mellemtrin', 'Mellemtrin', 'Skema til udfyldelse'),
  slot('school_completion', 'udskoling', 'Udskoling', 'Skema til udfyldelse'),
  // Assessments
  slot('assessment', 'abas_3_førskole_foraeldre_paedagog', 'ABAS-3 førskole (forældre/pædagog)', 'Assessments'),
  slot('assessment', 'abas_3_skole_foraeldre_laerer', 'ABAS-3 skole (forældre/lærer)', 'Assessments'),
  slot('assessment', 'abas_3_voksen_selv_paaroerende', 'ABAS-3 voksen (selv/pårørende)', 'Assessments'),
  slot('assessment', 'brief_2_førskole_foraeldre_paedagog', 'BRIEF-2 førskole (forældre/pædagog)', 'Assessments'),
  slot('assessment', 'brief_2_skole_foraeldre_laerer', 'BRIEF-2 skole (forældre/lærer)', 'Assessments'),
  slot('assessment', 'brief_2_voksen_selv_paaroerende', 'BRIEF-2 voksen (selv/pårørende)', 'Assessments'),
  slot('assessment', 'brief_selv', 'BRIEF + selv', 'Assessments'),
  slot('assessment', 'brief_srs_multi_besvarelse', 'BRIEF + SRS multi (besvarelse)', 'Assessments'),
  slot('assessment', 'srs_førskole_foraeldre_laerer', 'SRS førskole (forældre/lærer)', 'Assessments'),
  slot('assessment', 'srs_skole_foraeldre_laerer', 'SRS skole (forældre/lærer)', 'Assessments'),
  slot('assessment', 'vineland_førskole', 'VINELAND førskole', 'Assessments'),
  slot('assessment', 'vineland_skole', 'VINELAND skole', 'Assessments'),
];

function isChecked(
  obj: Record<string, unknown> | null | undefined,
  field: string,
): boolean {
  return Boolean(obj?.[field]);
}

/** Slots that are checked on Sagsakter (need files). */
export function getRequestedFileSlots(referral: Referral): ReferralFileSlot[] {
  const ppr = referral.ppr_documents as PPRDocuments | undefined;
  const hospital = referral.hospital_documents as HospitalDocuments | undefined;
  const paper = referral.paper_anamnese_forms as PaperAnamneseForms | undefined;
  const school = referral.school_completion_forms as SchoolCompletionForms | undefined;
  const assessment = referral.assessment_schemes as AssessmentSchemes | undefined;

  return ALL_REFERRAL_FILE_SLOTS.filter((s) => {
    switch (s.category) {
      case 'ppr':
        return isChecked(ppr as Record<string, unknown>, s.fieldName);
      case 'hospital':
        return isChecked(hospital as Record<string, unknown>, s.fieldName);
      case 'paper_anamnese':
        return isChecked(paper as Record<string, unknown>, s.fieldName);
      case 'school_completion':
        return isChecked(school as Record<string, unknown>, s.fieldName);
      case 'assessment':
        return isChecked(assessment as Record<string, unknown>, s.fieldName);
      default:
        return false;
    }
  });
}

export function findSlot(
  category: string | null | undefined,
  fieldName: string | null | undefined,
): ReferralFileSlot | undefined {
  if (!category || !fieldName) return undefined;
  return ALL_REFERRAL_FILE_SLOTS.find(
    (s) => s.category === category && s.fieldName === fieldName,
  );
}
